import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  Activity,
  Search,
  RefreshCw,
  Radio,
  User,
  Clock,
  Globe,
  ExternalLink,
  Pause,
  Play,
  Trash2,
  Eye,
  EyeOff,
  Copy,
  Check,
  SlidersHorizontal,
  AlertCircle,
  CheckCircle2,
  XCircle,
  X,
  FileCode,
  ArrowDownCircle,
  Lock,
  Unlock,
  MapPin,
  Smartphone,
  Laptop,
  FileSpreadsheet,
} from "lucide-react";
import { api } from "../api/staff";
import {
  connect,
  disconnect,
  subscribe,
  joinRoom,
  leaveRoom,
  isConnected,
  type ServerEnvelope,
} from "../lib/socket";
import { UserAvatar } from "../components/common/UserAvatar";

export interface GeoLocation {
  ip: string | null;
  country: string | null;
  countryName: string | null;
  flag: string | null;
  city: string | null;
  region: string | null;
  timezone: string | null;
}

export interface ClientPlatformInfo {
  kind: "desktop" | "mobile" | "tablet" | "bot" | "unknown";
  os: string;
  browser: string;
  platformName: string;
  telegramPlatform?: string | null;
  summary: string;
}

export interface AuditLogItem {
  id: string;
  eventType: string;
  userId: string | null;
  ip: string | null;
  userAgent: string | null;
  metadata: Record<string, any>;
  createdAt: string;
  channel?: "telegram_bot" | "telegram_mini_app" | "web";
  isTelegramAction?: boolean;
  geo?: GeoLocation;
  client?: ClientPlatformInfo;
  user?: {
    privateId: string;
    publicId: string | null;
    username: string | null;
    displayName: string | null;
    email?: string | null;
    status: string;
    avatarUrl?: string | null;
    createdAt?: string;
    isTelegram?: boolean;
    hasTelegramAccount?: boolean;
    telegramId?: string | null;
    telegramUsername?: string | null;
  } | null;
  isLive?: boolean;
}

export function getChannelBadge(channel?: string, isTelegramAction?: boolean) {
  if (channel === "telegram_bot") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#229ed9]/15 text-[#229ed9] border border-[#229ed9]/30" title="Action effectuée via le Bot Telegram">
        <span>🤖</span>
        <span>Bot Telegram</span>
      </span>
    );
  }
  if (channel === "telegram_mini_app") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-500/15 text-sky-400 border border-sky-500/30" title="Action effectuée via la Mini App Telegram">
        <span>📱</span>
        <span>Mini App TG</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" title="Action effectuée via le Site Web (Navigateur)">
      <span>🌐</span>
      <span>Site Web</span>
    </span>
  );
}

interface FilterState {
  search: string;
  category: string;
  statusRange: string;
  method: string;
  channel: string;
  onlySearch: boolean;
  onlyErrors: boolean;
  userId: string;
}

export function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Filters
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    category: "all",
    statusRange: "all",
    method: "all",
    channel: "all",
    onlySearch: false,
    onlyErrors: false,
    userId: "",
  });
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Live WebSocket
  const [wsConnected, setWsConnected] = useState(false);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  // Masking toggles for table preview
  const [unmaskedIps, setUnmaskedIps] = useState<Record<string, boolean>>({});

  // Inspection Modal
  const [selectedLog, setSelectedLog] = useState<AuditLogItem | null>(null);

  // Observer ref for infinite scroll
  const observerTarget = useRef<HTMLDivElement | null>(null);

  const fetchLogs = async (cursor?: string | null, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = new URLSearchParams();
      params.set("limit", "100");
      if (cursor) params.set("cursor", cursor);
      if (filters.search.trim()) params.set("search", filters.search.trim());
      if (filters.category !== "all") params.set("eventType", filters.category);
      if (filters.userId.trim()) params.set("userId", filters.userId.trim());

      const res = await api<{ items: AuditLogItem[]; nextCursor: string | null }>(
        `/staff/audit-logs?${params.toString()}`
      );

      if (res?.items) {
        setLogs((prev) => (reset ? res.items : [...prev, ...res.items]));
        setNextCursor(res.nextCursor);
        setHasMore(Boolean(res.nextCursor && res.items.length >= 100));
      }
    } catch (err) {
      console.error("Failed to load audit logs", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchLogs(null, true);
  }, [filters.category, filters.userId]);

  // Connect to live WebSocket and join staff:audit room
  useEffect(() => {
    connect();

    const checkInterval = setInterval(() => {
      setWsConnected(isConnected());
    }, 1000);

    joinRoom("staff:audit");

    const unsubscribe = subscribe((envelope: ServerEnvelope) => {
      if (envelope.type === "joined" && envelope.room === "staff:audit") {
        setWsConnected(true);
      }
      if (envelope.type === "audit_log.created" && envelope.data) {
        if (pausedRef.current) return;
        const newItem: AuditLogItem = {
          ...envelope.data,
          isLive: true,
        };
        setLogs((prev) => [newItem, ...prev]);
      }
    });

    return () => {
      clearInterval(checkInterval);
      leaveRoom("staff:audit");
      unsubscribe();
      disconnect();
    };
  }, []);

  // Infinite scroll trigger
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target?.isIntersecting && hasMore && !loadingMore && !loading && nextCursor) {
        fetchLogs(nextCursor, false);
      }
    },
    [hasMore, loadingMore, loading, nextCursor]
  );

  useEffect(() => {
    const el = observerTarget.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "200px",
      threshold: 0.1,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  // Filtered in-memory records
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const meta = log.metadata || {};

      // Filter out static files, scanner probes and non-business asset paths
      const pathLower = (meta.path || "").toLowerCase().split("?")[0];
      if (
        pathLower.startsWith("/_next") ||
        pathLower.startsWith("/static") ||
        pathLower.startsWith("/assets") ||
        pathLower.startsWith("/fonts") ||
        pathLower.startsWith("/images") ||
        pathLower.startsWith("/media") ||
        pathLower.startsWith("/favicon") ||
        pathLower.startsWith("/telegram/avatar") ||
        pathLower.startsWith("/api/telegram/avatar") ||
        pathLower.startsWith("/robots.txt") ||
        pathLower.startsWith("/sitemap.xml") ||
        pathLower.startsWith("/.") ||
        pathLower.includes("/wp-") ||
        pathLower.includes("phpmyadmin") ||
        pathLower.includes("/cgi-bin") ||
        /\.(js|mjs|cjs|css|map|png|jpg|jpeg|gif|svg|ico|webp|avif|woff|woff2|ttf|txt|xml|json|env|php)$/i.test(pathLower)
      ) {
        return false;
      }

      const status = typeof meta.statusCode === "number" ? meta.statusCode : null;
      const method = typeof meta.method === "string" ? meta.method.toUpperCase() : null;

      // Status range filter
      if (filters.statusRange === "2xx" && status && (status < 200 || status >= 300)) return false;
      if (filters.statusRange === "4xx" && status && (status < 400 || status >= 500)) return false;
      if (filters.statusRange === "5xx" && status && status < 500) return false;
      if (filters.statusRange === "200" && status !== 200) return false;

      // Method filter
      if (filters.method !== "all" && method && method !== filters.method) return false;

      // Channel / Platform filter
      if (filters.channel !== "all") {
        const itemChannel = log.channel || meta.channel || (meta.source === "telegram_bot" ? "telegram_bot" : "web");
        if (filters.channel === "web" && itemChannel !== "web") return false;
        if (filters.channel === "telegram_bot" && itemChannel !== "telegram_bot") return false;
        if (filters.channel === "telegram_mini_app" && itemChannel !== "telegram_mini_app") return false;
        if (filters.channel === "telegram" && itemChannel !== "telegram_bot" && itemChannel !== "telegram_mini_app") return false;
      }

      // Only search queries
      if (filters.onlySearch) {
        const isSearch =
          meta.isSearch ||
          meta.isUpload ||
          log.eventType === "upload.file_enrich" ||
          log.eventType.includes("search") ||
          meta.path?.includes("/enrich") ||
          meta.path?.includes("/search");
        if (!isSearch) return false;
      }

      // Only errors
      if (filters.onlyErrors) {
        if (status && status < 400) return false;
        if (!status && !log.eventType.includes("failed") && !log.eventType.includes("error")) return false;
      }

      // Text search
      if (filters.search.trim()) {
        const q = filters.search.toLowerCase();
        const matchEvent = log.eventType.toLowerCase().includes(q);
        const matchIp = log.ip?.toLowerCase().includes(q);
        const matchUa = log.userAgent?.toLowerCase().includes(q);
        const matchPath = meta.path?.toLowerCase().includes(q);
        const matchMethod = method?.toLowerCase().includes(q);
        const matchStatus = status ? status.toString().includes(q) : false;
        const matchFileName = meta.fileName ? String(meta.fileName).toLowerCase().includes(q) : false;
        const matchColumns = Array.isArray(meta.columns) ? meta.columns.join(" ").toLowerCase().includes(q) : false;
        const matchUser =
          log.user?.username?.toLowerCase().includes(q) ||
          log.user?.displayName?.toLowerCase().includes(q) ||
          log.user?.email?.toLowerCase().includes(q) ||
          log.userId?.toLowerCase().includes(q);
        const matchMeta = JSON.stringify(meta).toLowerCase().includes(q);

        return (
          matchEvent ||
          matchIp ||
          matchUa ||
          matchPath ||
          matchMethod ||
          matchStatus ||
          matchFileName ||
          matchColumns ||
          matchUser ||
          matchMeta
        );
      }

      return true;
    });
  }, [logs, filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.category !== "all") count++;
    if (filters.statusRange !== "all") count++;
    if (filters.method !== "all") count++;
    if (filters.channel !== "all") count++;
    if (filters.onlySearch) count++;
    if (filters.onlyErrors) count++;
    if (filters.userId.trim()) count++;
    return count;
  }, [filters]);

  const toggleIpMask = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setUnmaskedIps((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const maskIp = (ip?: string | null, unmasked = false) => {
    if (!ip) return "N/A";
    if (unmasked) return ip;
    const parts = ip.split(".");
    if (parts.length === 4) {
      return `${parts[0]}.${parts[1]}.***.***`;
    }
    return ip.slice(0, 8) + "...";
  };

  const getStatusBadge = (statusCode?: number | null) => {
    if (!statusCode) return null;
    if (statusCode >= 200 && statusCode < 300) {
      return (
        <span className="px-2 py-0.5 rounded-md font-mono text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          {statusCode}
        </span>
      );
    }
    if (statusCode >= 300 && statusCode < 400) {
      return (
        <span className="px-2 py-0.5 rounded-md font-mono text-xs font-bold bg-blue-500/15 text-blue-400 border border-blue-500/30">
          {statusCode}
        </span>
      );
    }
    if (statusCode >= 400 && statusCode < 500) {
      return (
        <span className="px-2 py-0.5 rounded-md font-mono text-xs font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {statusCode}
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-md font-mono text-xs font-bold bg-rose-500/15 text-rose-400 border border-rose-500/30 flex items-center gap-1">
        <XCircle className="w-3 h-3" />
        {statusCode}
      </span>
    );
  };

  const getMethodBadge = (method?: string | null) => {
    if (!method) return null;
    const m = method.toUpperCase();
    const colors: Record<string, string> = {
      GET: "bg-blue-500/15 text-blue-400 border-blue-500/30",
      POST: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
      PUT: "bg-amber-500/15 text-amber-400 border-amber-500/30",
      PATCH: "bg-amber-500/15 text-amber-400 border-amber-500/30",
      DELETE: "bg-rose-500/15 text-rose-400 border-rose-500/30",
    };
    return (
      <span
        className={`px-1.5 py-0.5 rounded text-[11px] font-mono font-bold border ${
          colors[m] || "bg-muted text-muted-foreground border-border"
        }`}
      >
        {m}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <Activity className="w-6 h-6 text-primary" />
              Audit Logs & Traçabilité Directe
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              Surveillance Système
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Traçabilité intégrale de toutes les requêtes HTTP, statuts 200/erreurs, connexions et recherches en direct.
          </p>
        </div>

        {/* Live Controls */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium shadow-xs ${
              wsConnected
                ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
                : "bg-amber-500/10 text-amber-500 border-amber-500/20"
            }`}
          >
            <Radio className={`w-3.5 h-3.5 ${wsConnected ? "animate-pulse" : ""}`} />
            <span>{wsConnected ? "Flux WS Actif" : "Connexion WS..."}</span>
          </div>

          <button
            onClick={() => setPaused(!paused)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
              paused
                ? "bg-amber-500/10 text-amber-500 border-amber-500/20 hover:bg-amber-500/20"
                : "bg-muted text-muted-foreground border-border hover:bg-accent"
            }`}
          >
            {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            {paused ? "Reprendre" : "Pause"}
          </button>

          <button
            onClick={() => setLogs([])}
            className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            title="Effacer l'affichage local"
          >
            <Trash2 className="w-4 h-4" />
          </button>

          <button
            onClick={() => fetchLogs(null, true)}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors shadow-xs disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        {/* Search input */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Rechercher par route, statut (200), IP, utilisateur, payload..."
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-primary shadow-xs"
          />
        </div>

        {/* Quick filters & Modal button */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors shadow-xs ${
              activeFilterCount > 0
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card text-foreground border-border hover:bg-accent"
            }`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span>Filtres avancés</span>
            {activeFilterCount > 0 && (
              <span className="w-4 h-4 rounded-full bg-white text-primary text-[10px] font-bold flex items-center justify-center ml-1">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Quick Platform pills */}
          <button
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                channel: prev.channel === "web" ? "all" : "web",
              }))
            }
            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              filters.channel === "web"
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40"
                : "bg-card text-muted-foreground border-border hover:bg-accent"
            }`}
          >
            🌐 Web
          </button>

          <button
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                channel: prev.channel === "telegram_bot" ? "all" : "telegram_bot",
              }))
            }
            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              filters.channel === "telegram_bot"
                ? "bg-[#229ed9]/25 text-[#229ed9] border-[#229ed9]/40"
                : "bg-card text-muted-foreground border-border hover:bg-accent"
            }`}
          >
            🤖 Bot TG
          </button>

          <button
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                channel: prev.channel === "telegram_mini_app" ? "all" : "telegram_mini_app",
              }))
            }
            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              filters.channel === "telegram_mini_app"
                ? "bg-sky-500/25 text-sky-400 border-sky-500/40"
                : "bg-card text-muted-foreground border-border hover:bg-accent"
            }`}
          >
            📱 Mini App TG
          </button>

          {/* Quick status 200 pill */}
          <button
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                statusRange: prev.statusRange === "200" ? "all" : "200",
              }))
            }
            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              filters.statusRange === "200"
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40"
                : "bg-card text-muted-foreground border-border hover:bg-accent"
            }`}
          >
            200 OK
          </button>

          {/* Quick Searches pill */}
          <button
            onClick={() => setFilters((prev) => ({ ...prev, onlySearch: !prev.onlySearch }))}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              filters.onlySearch
                ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/40"
                : "bg-card text-muted-foreground border-border hover:bg-accent"
            }`}
          >
            🔍 Recherches
          </button>

          {/* Quick Errors pill */}
          <button
            onClick={() => setFilters((prev) => ({ ...prev, onlyErrors: !prev.onlyErrors }))}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              filters.onlyErrors
                ? "bg-rose-500/20 text-rose-400 border-rose-500/40"
                : "bg-card text-muted-foreground border-border hover:bg-accent"
            }`}
          >
            ⚠️ Erreurs
          </button>

          {activeFilterCount > 0 && (
            <button
              onClick={() =>
                setFilters({
                  search: "",
                  category: "all",
                  statusRange: "all",
                  method: "all",
                  channel: "all",
                  onlySearch: false,
                  onlyErrors: false,
                  userId: "",
                })
              }
              className="text-xs text-muted-foreground hover:text-foreground underline px-1.5"
            >
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Main Continuous Feed Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-xs">
        <div className="px-4 py-3 border-b border-border bg-muted/20 flex items-center justify-between">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <span>Requêtes & Événements capturés</span>
            <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary font-mono text-[11px]">
              {filteredLogs.length}
            </span>
          </div>
          {paused && (
            <div className="text-xs text-amber-500 font-medium flex items-center gap-1.5">
              <Pause className="w-3 h-3" /> Flux en pause
            </div>
          )}
        </div>

        {loading && logs.length === 0 ? (
          <div className="py-24 flex flex-col items-center justify-center text-muted-foreground">
            <RefreshCw className="w-8 h-8 animate-spin text-primary mb-3" />
            <p className="text-sm font-medium">Chargement du journal d'audit...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="py-24 text-center text-muted-foreground">
            <Activity className="w-10 h-10 mx-auto opacity-30 mb-2" />
            <p className="text-sm font-medium">Aucune requête ne correspond aux critères.</p>
            {activeFilterCount > 0 && (
              <p className="text-xs mt-1 text-muted-foreground/70">
                Essayez d'élargir les filtres de recherche.
              </p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredLogs.map((log) => {
              const meta = log.metadata || {};
              const statusCode = typeof meta.statusCode === "number" ? meta.statusCode : null;
              const method = typeof meta.method === "string" ? meta.method : null;
              const durationMs = typeof meta.durationMs === "number" ? meta.durationMs : null;
              const path = typeof meta.path === "string" ? meta.path : null;
              const isUpload = Boolean(meta.isUpload || log.eventType === "upload.file_enrich");
              const isSearch =
                !isUpload &&
                (meta.isSearch ||
                log.eventType.includes("search") ||
                path?.includes("/enrich") ||
                path?.includes("/search"));
              const isIpUnmasked = Boolean(unmaskedIps[log.id]);

              return (
                <div
                  key={log.id}
                  onClick={() => setSelectedLog(log)}
                  className={`p-3.5 transition-colors cursor-pointer hover:bg-muted/40 ${
                    log.isLive ? "bg-primary/[0.04]" : ""
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    {/* Left: Status, Method, Path, User & Summary */}
                    <div className="flex items-start gap-3 min-w-0">
                      {/* Status / Method Badge block */}
                      <div className="flex flex-col gap-1 shrink-0 pt-0.5">
                        {isUpload ? (
                          <>
                            <span className="px-2 py-0.5 rounded-md font-mono text-xs font-bold bg-violet-500/15 text-violet-400 border border-violet-500/30 flex items-center gap-1">
                              <FileSpreadsheet className="w-3 h-3" />
                              UPLOAD
                            </span>
                            <span className="px-1.5 py-0.5 rounded text-[11px] font-mono font-bold border bg-violet-500/15 text-violet-300 border-violet-500/30 text-center">
                              BATCH
                            </span>
                          </>
                        ) : (
                          <>
                            {statusCode ? (
                              getStatusBadge(statusCode)
                            ) : (
                              <span className="px-2 py-0.5 rounded-md font-mono text-xs font-semibold bg-muted text-muted-foreground border border-border">
                                LOG
                              </span>
                            )}
                            {method && getMethodBadge(method)}
                          </>
                        )}
                      </div>

                      {/* Content block */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Endpoint Path or Event Type */}
                          {isUpload ? (
                            <span className="font-mono text-xs font-bold text-violet-300 flex items-center gap-1.5">
                              <span>📂 Import Fichier • {meta.totalRows ?? 0} contact(s)</span>
                              {meta.fileName && (
                                <span className="text-muted-foreground font-normal">({meta.fileName})</span>
                              )}
                            </span>
                          ) : path ? (
                            <span className="font-mono text-xs font-bold text-foreground">
                              {path}
                            </span>
                          ) : (
                            <span className="font-mono text-xs font-bold text-foreground">
                              {log.eventType}
                            </span>
                          )}

                          {log.eventType && path && !isUpload && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-mono text-muted-foreground bg-muted border border-border">
                              {log.eventType}
                            </span>
                          )}

                          {log.isLive && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 animate-pulse">
                              LIVE
                            </span>
                          )}

                          {durationMs !== null && (
                            <span className="text-[11px] font-mono text-muted-foreground">
                              {durationMs}ms
                            </span>
                          )}
                        </div>

                        {/* File Upload Preview */}
                        {isUpload && (
                          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                            <span className="px-2 py-0.5 rounded bg-violet-500/10 text-violet-300 border border-violet-500/20 text-[11px] font-semibold flex items-center gap-1">
                              <FileSpreadsheet className="w-3 h-3" />
                              Fichier CSV / Excel ({meta.totalRows ?? 0} lignes)
                            </span>
                            {Array.isArray(meta.columns) && meta.columns.length > 0 && (
                              <span className="text-xs text-muted-foreground font-mono">
                                Colonnes : {meta.columns.slice(0, 4).join(", ")}{meta.columns.length > 4 ? "..." : ""}
                              </span>
                            )}
                            {meta.isTrial && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 font-medium">
                                Essai gratuit
                              </span>
                            )}
                          </div>
                        )}

                        {/* Search criteria protected preview */}
                        {isSearch && (
                          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                            <span className="px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 text-[11px] font-semibold flex items-center gap-1">
                              <Lock className="w-3 h-3" />
                              Recherche protégée
                            </span>
                            <span className="text-xs text-muted-foreground italic">
                              (Contenu masqué pour confidentialité — Cliquer pour inspecter)
                            </span>
                          </div>
                        )}

                        {/* User Identity & IP Line */}
                        <div className="flex items-center gap-2.5 mt-1.5 text-xs text-muted-foreground flex-wrap">
                          {/* Channel / Platform badge */}
                          {getChannelBadge(log.channel || meta.channel || meta.source, log.isTelegramAction)}

                          {/* User Avatar + Name */}
                          {log.user ? (
                            <div className="flex items-center gap-1.5 font-medium text-foreground">
                              <UserAvatar
                                user={{
                                  displayName: log.user.displayName,
                                  username: log.user.username,
                                  email: log.user.email,
                                  avatarUrl: log.user.avatarUrl,
                                }}
                                size="sm"
                              />
                              <span>
                                {log.user.displayName || log.user.username || "Utilisateur"}
                              </span>
                              {(log.isTelegramAction || log.user.isTelegram) ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#229ed9]/15 text-[#229ed9] border border-[#229ed9]/30">
                                  <span>✈️</span>
                                  <span>{log.user.telegramUsername ? `@${log.user.telegramUsername}` : (log.user.telegramId ? `TG #${log.user.telegramId}` : "Telegram")}</span>
                                </span>
                              ) : log.user.publicId ? (
                                <span className="font-mono text-[10px] text-muted-foreground">
                                  #{log.user.publicId}
                                </span>
                              ) : null}
                            </div>
                          ) : (meta.source === "telegram_bot" || meta.chatId || meta.telegramId || meta.source === "telegram_mini_app") ? (
                            <div className="flex items-center gap-1.5 font-medium text-foreground">
                              <UserAvatar
                                user={{
                                  displayName: meta.telegramUsername ? `@${meta.telegramUsername}` : `Telegram #${meta.chatId || meta.telegramId}`,
                                  avatarUrl: (meta.chatId || meta.telegramId) ? `/api/telegram/avatar/${meta.chatId || meta.telegramId}` : null,
                                }}
                                size="sm"
                              />
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#229ed9]/15 text-[#229ed9] border border-[#229ed9]/30">
                                <span>✈️</span>
                                <span>{meta.telegramUsername ? `@${meta.telegramUsername}` : `TG #${meta.chatId || meta.telegramId}`}</span>
                              </span>
                            </div>
                          ) : log.userId ? (
                            <span className="flex items-center gap-1 font-mono text-[11px]">
                              <User className="w-3 h-3 text-muted-foreground" />
                              {log.userId.slice(0, 8)}...
                            </span>
                          ) : (
                            <span className="text-muted-foreground/60 italic">Anonyme / API</span>
                          )}

                          {/* Client IP (Masked with toggle) */}
                          {log.ip && (
                            <span
                              onClick={(e) => toggleIpMask(log.id, e)}
                              className="flex items-center gap-1 font-mono text-[11px] hover:text-foreground transition-colors cursor-pointer"
                              title={isIpUnmasked ? "Masquer l'IP" : "Afficher l'IP"}
                            >
                              <Globe className="w-3 h-3 text-muted-foreground" />
                              <span>{maskIp(log.ip, isIpUnmasked)}</span>
                              {isIpUnmasked ? (
                                <EyeOff className="w-3 h-3 text-primary" />
                              ) : (
                                <Eye className="w-3 h-3 text-muted-foreground/70" />
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right: Date, Time and Action */}
                    <div className="flex items-center justify-between lg:justify-end gap-3 shrink-0 pt-1 lg:pt-0">
                      <div className="text-left lg:text-right">
                        <div className="text-xs font-mono text-foreground flex items-center lg:justify-end gap-1">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          {new Date(log.createdAt).toLocaleTimeString()}
                        </div>
                        <div className="text-[11px] text-muted-foreground font-mono">
                          {new Date(log.createdAt).toLocaleDateString()}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLog(log);
                        }}
                        className="px-2.5 py-1.5 rounded-lg bg-accent/60 hover:bg-accent text-xs font-semibold text-foreground border border-border flex items-center gap-1 transition-colors"
                      >
                        Inspecter
                        <ExternalLink className="w-3 h-3 text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Infinite Scroll Trigger */}
            <div ref={observerTarget} className="p-4 text-center">
              {loadingMore ? (
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-primary" />
                  <span>Chargement des requêtes suivantes...</span>
                </div>
              ) : hasMore ? (
                <button
                  onClick={() => nextCursor && fetchLogs(nextCursor, false)}
                  className="px-4 py-2 rounded-lg bg-card border border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-accent transition-colors flex items-center gap-1.5 mx-auto"
                >
                  <ArrowDownCircle className="w-4 h-4 text-primary" />
                  Charger plus d'historique
                </button>
              ) : (
                <p className="text-xs text-muted-foreground/60 italic">
                  Fin de l'historique d'audit.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* FILTER MODAL */}
      {isFilterModalOpen && (
        <AuditFilterModal
          filters={filters}
          onApply={(newFilters) => {
            setFilters(newFilters);
            setIsFilterModalOpen(false);
          }}
          onClose={() => setIsFilterModalOpen(false)}
        />
      )}

      {/* REQUEST DETAIL INSPECTION MODAL */}
      {selectedLog && (
        <RequestDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </div>
  );
}

// -------------------------------------------------------------
// FILTER MODAL COMPONENT
// -------------------------------------------------------------
interface AuditFilterModalProps {
  filters: FilterState;
  onApply: (f: FilterState) => void;
  onClose: () => void;
}

function AuditFilterModal({ filters: initialFilters, onApply, onClose }: AuditFilterModalProps) {
  const [draft, setDraft] = useState<FilterState>({ ...initialFilters });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in-50 zoom-in-95">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/20">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-primary" />
            <h2 className="font-bold text-lg text-foreground">Filtres avancés</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Status Range */}
          <div>
            <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              Code Statut HTTP
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { id: "all", label: "Tous" },
                { id: "200", label: "200 OK" },
                { id: "2xx", label: "2xx Succès" },
                { id: "4xx", label: "4xx Erreurs" },
                { id: "5xx", label: "5xx Serveur" },
              ].map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, statusRange: s.id }))}
                  className={`py-2 px-3 rounded-lg text-xs font-semibold border transition-colors ${
                    draft.statusRange === s.id
                      ? "bg-primary text-primary-foreground border-primary shadow-xs"
                      : "bg-muted/40 text-muted-foreground border-border hover:bg-accent hover:text-foreground"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Channel / Platform */}
          <div>
            <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              Canal / Plateforme
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { id: "all", label: "Tous" },
                { id: "web", label: "🌐 Web" },
                { id: "telegram_bot", label: "🤖 Bot TG" },
                { id: "telegram_mini_app", label: "📱 Mini App" },
              ].map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, channel: c.id }))}
                  className={`py-2 px-2 rounded-lg text-xs font-semibold border transition-colors ${
                    draft.channel === c.id
                      ? "bg-primary text-primary-foreground border-primary shadow-xs"
                      : "bg-muted/40 text-muted-foreground border-border hover:bg-accent hover:text-foreground"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* HTTP Method */}
          <div>
            <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              Méthode HTTP
            </label>
            <div className="grid grid-cols-5 gap-2">
              {["all", "GET", "POST", "PUT", "DELETE"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, method: m }))}
                  className={`py-2 px-2 rounded-lg text-xs font-semibold border font-mono transition-colors ${
                    draft.method === m
                      ? "bg-primary text-primary-foreground border-primary shadow-xs"
                      : "bg-muted/40 text-muted-foreground border-border hover:bg-accent hover:text-foreground"
                  }`}
                >
                  {m === "all" ? "Toutes" : m}
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              Catégorie d'événement
            </label>
            <select
              value={draft.category}
              onChange={(e) => setDraft((prev) => ({ ...prev, category: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-card border border-border text-sm text-foreground focus:outline-hidden focus:ring-1 focus:ring-primary"
            >
              <option value="all">Toutes les catégories</option>
              <option value="http">Requêtes HTTP</option>
              <option value="search">Recherches (CSINT, OSINT, Enrich)</option>
              <option value="auth">Connexions & Authentification</option>
              <option value="staff">Actions Staff / Modération</option>
              <option value="passkey">Passkeys & Clés WebAuthn</option>
              <option value="payment">Paiements & Abonnements</option>
            </select>
          </div>

          {/* Target User ID */}
          <div>
            <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1.5">
              Filtrer par Utilisateur (ID ou Username)
            </label>
            <input
              type="text"
              placeholder="Ex: c1234567-89ab... ou nom d'utilisateur"
              value={draft.userId}
              onChange={(e) => setDraft((prev) => ({ ...prev, userId: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-card border border-border text-sm text-foreground focus:outline-hidden focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Quick Checkboxes */}
          <div className="space-y-3 pt-2 border-t border-border">
            <label className="flex items-center gap-2.5 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={draft.onlySearch}
                onChange={(e) => setDraft((prev) => ({ ...prev, onlySearch: e.target.checked }))}
                className="w-4 h-4 rounded text-primary focus:ring-primary border-border bg-card"
              />
              <span className="font-medium text-foreground">
                Afficher uniquement les requêtes de recherche
              </span>
            </label>

            <label className="flex items-center gap-2.5 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={draft.onlyErrors}
                onChange={(e) => setDraft((prev) => ({ ...prev, onlyErrors: e.target.checked }))}
                className="w-4 h-4 rounded text-primary focus:ring-primary border-border bg-card"
              />
              <span className="font-medium text-foreground">
                Afficher uniquement les erreurs (Statut &ge; 400 ou échecs)
              </span>
            </label>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/20 flex items-center justify-between">
          <button
            type="button"
            onClick={() =>
              setDraft({
                search: "",
                category: "all",
                statusRange: "all",
                method: "all",
                channel: "all",
                onlySearch: false,
                onlyErrors: false,
                userId: "",
              })
            }
            className="text-xs text-muted-foreground hover:text-foreground font-medium underline"
          >
            Réinitialiser tout
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-border text-xs font-semibold text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={() => onApply(draft)}
              className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs"
            >
              Appliquer les filtres
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// REQUEST DETAIL INSPECTION MODAL COMPONENT
// -------------------------------------------------------------
interface RequestDetailModalProps {
  log: AuditLogItem;
  onClose: () => void;
}

function RequestDetailModal({ log, onClose }: RequestDetailModalProps) {
  const [unmaskSearch, setUnmaskSearch] = useState(false);
  const [unmaskIp, setUnmaskIp] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const meta = log.metadata || {};
  const statusCode = typeof meta.statusCode === "number" ? meta.statusCode : null;
  const method = typeof meta.method === "string" ? meta.method.toUpperCase() : null;
  const durationMs = typeof meta.durationMs === "number" ? meta.durationMs : null;
  const path = typeof meta.path === "string" ? meta.path : null;
  const isUpload = Boolean(meta.isUpload || log.eventType === "upload.file_enrich");

  // Search payload detection
  const isSearch =
    !isUpload &&
    (meta.isSearch ||
    log.eventType.includes("search") ||
    path?.includes("/enrich") ||
    path?.includes("/search"));

  const searchData = meta.body || meta.searchPayload || meta.query || null;

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden animate-in fade-in-50 zoom-in-95 flex flex-col max-h-[90vh]">
        {/* Modal Top Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/20 shrink-0">
          <div className="flex items-center gap-3">
            {isUpload ? (
              <span className="px-2.5 py-1 rounded-md font-mono text-sm font-bold border bg-violet-500/15 text-violet-400 border-violet-500/30 flex items-center gap-1">
                <FileSpreadsheet className="w-4 h-4" />
                UPLOAD BATCH
              </span>
            ) : statusCode ? (
              <span
                className={`px-2.5 py-1 rounded-md font-mono text-sm font-bold border ${
                  statusCode >= 200 && statusCode < 300
                    ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                    : statusCode >= 400
                    ? "bg-rose-500/15 text-rose-400 border-rose-500/30"
                    : "bg-blue-500/15 text-blue-400 border-blue-500/30"
                }`}
              >
                {statusCode}
              </span>
            ) : null}

            {method && !isUpload && (
              <span className="px-2 py-0.5 rounded font-mono text-xs font-bold bg-muted border border-border text-foreground">
                {method}
              </span>
            )}

            {getChannelBadge(log.channel || meta.channel || meta.source, log.isTelegramAction)}

            <div>
              <h2 className="font-mono text-sm font-bold text-foreground">
                {isUpload ? `📂 Import de Fichier (${meta.fileName || "fichier.csv"})` : (path || log.eventType)}
              </h2>
              <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                <span>ID: #{log.id}</span>
                <span>•</span>
                <span>{new Date(log.createdAt).toLocaleString()}</span>
                {durationMs !== null && (
                  <>
                    <span>•</span>
                    <span className="font-mono text-primary font-medium">{durationMs}ms</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* USER PROFILE SECTION */}
          <div className="p-4 rounded-xl border border-border bg-muted/20">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Profil Utilisateur Associé
              </span>
              {log.user && (
                <a
                  href={`/users`}
                  className="text-xs text-primary font-semibold hover:underline flex items-center gap-1"
                >
                  Voir dans Utilisateurs
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            {log.user ? (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3.5">
                  <UserAvatar
                    user={{
                      displayName: log.user.displayName,
                      username: log.user.username,
                      email: log.user.email,
                      avatarUrl: log.user.avatarUrl,
                    }}
                    size="lg"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-foreground">
                        {log.user.displayName || "Sans nom"}
                      </span>
                      {log.user.username && (
                        <span className="text-xs text-muted-foreground">
                          @{log.user.username}
                        </span>
                      )}
                      {getChannelBadge(log.channel || meta.channel || meta.source, log.isTelegramAction)}
                      {(log.isTelegramAction || log.user.isTelegram) && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#229ed9]/15 text-[#229ed9] border border-[#229ed9]/30 flex items-center gap-1">
                          ✈️ {log.user.telegramUsername ? `@${log.user.telegramUsername}` : (log.user.telegramId ? `TG #${log.user.telegramId}` : "Telegram")}
                        </span>
                      )}
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        {log.user.status}
                      </span>
                    </div>

                    <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1 font-mono flex-wrap">
                      {log.user.publicId && <span>Public ID: #{log.user.publicId}</span>}
                      {log.user.telegramId && <span>TG ID: {log.user.telegramId}</span>}
                      {log.user.telegramUsername && (
                        <a
                          href={`https://t.me/${log.user.telegramUsername}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#229ed9] hover:underline"
                        >
                          t.me/{log.user.telegramUsername}
                        </a>
                      )}
                      {log.user.hasTelegramAccount && !log.isTelegramAction && (
                        <span className="text-[#229ed9] font-sans">
                          (Compte Telegram lié)
                        </span>
                      )}
                      {log.user.email && <span>Email: {log.user.email}</span>}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => copyToClipboard(log.userId || "", "user_id")}
                  className="px-2.5 py-1.5 rounded-lg border border-border bg-card text-xs font-mono text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors"
                >
                  {copiedKey === "user_id" ? (
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  UUID: {log.userId?.slice(0, 10)}...
                </button>
              </div>
            ) : (meta.source === "telegram_bot" || meta.chatId || meta.telegramId) ? (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3.5">
                  <UserAvatar
                    user={{
                      displayName: meta.telegramUsername ? `@${meta.telegramUsername}` : `Telegram #${meta.chatId || meta.telegramId}`,
                      avatarUrl: (meta.chatId || meta.telegramId) ? `/api/telegram/avatar/${meta.chatId || meta.telegramId}` : null,
                    }}
                    size="lg"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-foreground">
                        {meta.telegramUsername ? `@${meta.telegramUsername}` : `Telegram #${meta.chatId || meta.telegramId}`}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#229ed9]/15 text-[#229ed9] border border-[#229ed9]/30 flex items-center gap-1">
                        ✈️ Telegram Bot
                      </span>
                    </div>

                    <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1 font-mono flex-wrap">
                      <span>Chat ID: {meta.chatId || meta.telegramId}</span>
                      {meta.telegramUsername && (
                        <a
                          href={`https://t.me/${meta.telegramUsername}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#229ed9] hover:underline"
                        >
                          t.me/{meta.telegramUsername}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : log.userId ? (
              <div className="flex items-center justify-between text-sm">
                <span className="font-mono text-muted-foreground">ID Utilisateur: {log.userId}</span>
                <button
                  type="button"
                  onClick={() => copyToClipboard(log.userId || "", "user_id")}
                  className="p-1.5 rounded text-muted-foreground hover:text-foreground"
                >
                  {copiedKey === "user_id" ? (
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                Requête anonyme ou exécutée via une clé d'API / Session publique.
              </p>
            )}
          </div>

          {/* FILE UPLOAD DETAILS */}
          {isUpload && (
            <div className="p-4 rounded-xl border border-violet-500/30 bg-violet-500/5 space-y-3">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-violet-400" />
                <span className="text-xs font-bold uppercase tracking-wider text-violet-300">
                  Détails de l'Import de Fichier (Upload Consolidé)
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="p-2.5 rounded-lg bg-card border border-border">
                  <span className="text-muted-foreground block text-[11px]">Nom du fichier :</span>
                  <span className="font-semibold font-mono text-foreground text-sm break-all">
                    {meta.fileName || "Inconnu"}
                  </span>
                </div>

                <div className="p-2.5 rounded-lg bg-card border border-border">
                  <span className="text-muted-foreground block text-[11px]">Nombre total de contacts / lignes :</span>
                  <span className="font-bold text-violet-400 text-sm">
                    {meta.totalRows ?? 0} contact(s)
                  </span>
                </div>

                <div className="p-2.5 rounded-lg bg-card border border-border sm:col-span-2">
                  <span className="text-muted-foreground block text-[11px] mb-1">Colonnes associées :</span>
                  <div className="flex flex-wrap gap-1.5">
                    {Array.isArray(meta.columns) && meta.columns.length > 0 ? (
                      meta.columns.map((col: string, i: number) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 rounded bg-muted font-mono text-[11px] text-foreground border border-border"
                        >
                          {col}
                        </span>
                      ))
                    ) : (
                      <span className="text-muted-foreground italic">Aucune colonne spécifiée</span>
                    )}
                  </div>
                </div>

                {meta.uploadSessionId && (
                  <div className="p-2.5 rounded-lg bg-card border border-border">
                    <span className="text-muted-foreground block text-[11px]">ID Session Upload :</span>
                    <span className="font-mono text-[11px] text-foreground break-all">
                      {meta.uploadSessionId}
                    </span>
                  </div>
                )}

                <div className="p-2.5 rounded-lg bg-card border border-border">
                  <span className="text-muted-foreground block text-[11px]">Formule / Mode :</span>
                  <span className="font-semibold text-foreground">
                    {meta.isTrial ? "⚡ Essai Gratuit (5 contacts)" : "⭐ Formule Pro+ (Enrichissement complet)"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* SEARCH CONTENT & CRITERIA BLOCK */}
          {isSearch && (
            <div className="p-4 rounded-xl border border-cyan-500/20 bg-cyan-500/5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs font-bold uppercase tracking-wider text-cyan-300">
                    Contenu & Critères de la Recherche
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => setUnmaskSearch(!unmaskSearch)}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-card border border-cyan-500/30 text-xs font-semibold text-cyan-300 hover:bg-cyan-500/10 transition-colors"
                >
                  {unmaskSearch ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  {unmaskSearch ? "Masquer les données" : "Démasquer le contenu"}
                </button>
              </div>

              {unmaskSearch ? (
                <div className="p-3 rounded-lg bg-card border border-border font-mono text-xs text-foreground overflow-x-auto">
                  <pre className="whitespace-pre-wrap">
                    {JSON.stringify(searchData, null, 2)}
                  </pre>
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-card/60 border border-dashed border-cyan-500/30 text-center">
                  <Lock className="w-6 h-6 text-cyan-400/50 mx-auto mb-1.5" />
                  <p className="text-xs font-medium text-foreground">
                    Données de recherche masquées par défaut.
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Cliquez sur "Démasquer le contenu" ci-dessus pour inspecter les critères recherchés.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* DEVICE, PLATFORM & GEOLOCATION */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Plateforme, Appareil & Navigateur */}
            <div className="p-4 rounded-xl border border-border bg-muted/10 space-y-3">
              <div className="flex items-center gap-2">
                <Laptop className="w-4 h-4 text-primary" />
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Plateforme, Appareil & Navigateur
                </span>
              </div>

              <div className="space-y-2 text-xs">
                <div className="flex justify-between items-center py-1 border-b border-border/40">
                  <span className="text-muted-foreground">Canal d'accès:</span>
                  <div>
                    {getChannelBadge(log.channel || meta.channel || meta.source, log.isTelegramAction)}
                  </div>
                </div>

                <div className="flex justify-between items-center py-1 border-b border-border/40">
                  <span className="text-muted-foreground">Type d'appareil:</span>
                  <span className="font-semibold text-foreground">
                    {(log.client || meta.client)?.kind === "mobile"
                      ? "📱 Mobile (Téléphone)"
                      : (log.client || meta.client)?.kind === "tablet"
                      ? "📟 Tablette"
                      : (log.client || meta.client)?.kind === "desktop"
                      ? "💻 Ordinateur de bureau"
                      : "Inconnu"}
                  </span>
                </div>

                <div className="flex justify-between items-center py-1 border-b border-border/40">
                  <span className="text-muted-foreground">Modèle / Plateforme:</span>
                  <span className="font-semibold font-mono text-foreground">
                    {(log.client || meta.client)?.platformName || "N/A"}
                  </span>
                </div>

                <div className="flex justify-between items-center py-1 border-b border-border/40">
                  <span className="text-muted-foreground">Système (OS):</span>
                  <span className="font-semibold font-mono text-foreground">
                    {(log.client || meta.client)?.os || "N/A"}
                  </span>
                </div>

                <div className="flex justify-between items-center py-1">
                  <span className="text-muted-foreground">Navigateur:</span>
                  <span className="font-semibold font-mono text-primary">
                    {(log.client || meta.client)?.browser || "N/A"}
                  </span>
                </div>

                {(log.client || meta.client)?.telegramPlatform && (
                  <div className="flex justify-between items-center py-1 border-t border-border/40">
                    <span className="text-muted-foreground">Plateforme Telegram Mini App:</span>
                    <span className="font-semibold text-[#229ed9]">
                      ✈️ {(log.client || meta.client)?.telegramPlatform}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Géolocalisation & Réseau */}
            <div className="p-4 rounded-xl border border-border bg-muted/10 space-y-3">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Localisation & Réseau
                </span>
              </div>

              <div className="space-y-2 text-xs">
                <div className="flex justify-between items-center py-1 border-b border-border/40">
                  <span className="text-muted-foreground">Pays:</span>
                  <div className="flex items-center gap-1 font-semibold text-foreground">
                    {(log.geo || meta.geo)?.flag && <span>{(log.geo || meta.geo)?.flag}</span>}
                    <span>{(log.geo || meta.geo)?.countryName || (log.geo || meta.geo)?.country || "Inconnu"}</span>
                    {(log.geo || meta.geo)?.country && (
                      <span className="text-[10px] text-muted-foreground font-mono">
                        ({(log.geo || meta.geo)?.country})
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center py-1 border-b border-border/40">
                  <span className="text-muted-foreground">Ville:</span>
                  <span className="font-semibold text-foreground">
                    {(log.geo || meta.geo)?.city || "Non résolue"}
                  </span>
                </div>

                {(log.geo || meta.geo)?.region && (
                  <div className="flex justify-between items-center py-1 border-b border-border/40">
                    <span className="text-muted-foreground">Région:</span>
                    <span className="text-foreground">{(log.geo || meta.geo)?.region}</span>
                  </div>
                )}

                {(log.geo || meta.geo)?.timezone && (
                  <div className="flex justify-between items-center py-1 border-b border-border/40">
                    <span className="text-muted-foreground">Fuseau horaire:</span>
                    <span className="font-mono text-muted-foreground">
                      {(log.geo || meta.geo)?.timezone}
                    </span>
                  </div>
                )}

                <div className="flex justify-between items-center py-1">
                  <span className="text-muted-foreground">Adresse IP:</span>
                  <div className="flex items-center gap-1 font-mono text-foreground font-semibold">
                    <span>{unmaskIp ? log.ip || "N/A" : log.ip?.replace(/\.\d+$/, ".***") || "N/A"}</span>
                    <button
                      type="button"
                      onClick={() => setUnmaskIp(!unmaskIp)}
                      className="p-1 rounded text-muted-foreground hover:text-foreground"
                      title={unmaskIp ? "Masquer" : "Démasquer"}
                    >
                      {unmaskIp ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    {log.ip && (
                      <button
                        type="button"
                        onClick={() => copyToClipboard(log.ip || "", "ip")}
                        className="p-1 rounded text-muted-foreground hover:text-foreground"
                        title="Copier l'IP"
                      >
                        {copiedKey === "ip" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RAW USER AGENT & HEADERS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* User Agent */}
            <div className="p-4 rounded-xl border border-border bg-muted/10 space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">
                User Agent Brut
              </span>
              <div className="p-2.5 rounded-lg bg-card border border-border font-mono text-[11px] text-muted-foreground break-all max-h-28 overflow-y-auto">
                {log.userAgent || "Non spécifié"}
              </div>
            </div>

            {/* Request Headers */}
            <div className="p-4 rounded-xl border border-border bg-muted/10 space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">
                En-têtes HTTP Archivés
              </span>
              <div className="p-2.5 rounded-lg bg-card border border-border font-mono text-[11px] space-y-1 text-foreground overflow-y-auto max-h-28">
                {meta.headers ? (
                  Object.entries(meta.headers).map(([k, v]) => (
                    <div key={k} className="flex items-start gap-1">
                      <strong className="text-muted-foreground">{k}:</strong>
                      <span className="break-all">{String(v || "")}</span>
                    </div>
                  ))
                ) : (
                  <span className="text-muted-foreground italic">Aucun en-tête archivé</span>
                )}
              </div>
            </div>
          </div>

          {/* FULL METADATA JSON VIEWER */}
          <div className="p-4 rounded-xl border border-border bg-muted/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <FileCode className="w-4 h-4 text-primary" />
                Payload Complet (JSON)
              </span>

              <button
                type="button"
                onClick={() =>
                  copyToClipboard(JSON.stringify(log.metadata, null, 2), "payload")
                }
                className="flex items-center gap-1 px-2.5 py-1 rounded bg-card border border-border text-xs font-medium text-foreground hover:bg-accent transition-colors"
              >
                {copiedKey === "payload" ? (
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
                Copier le JSON
              </button>
            </div>

            <pre className="p-3.5 rounded-lg bg-card border border-border font-mono text-xs text-foreground overflow-x-auto max-h-64 scrollbar-hide">
              {JSON.stringify(log.metadata, null, 2)}
            </pre>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3.5 border-t border-border bg-muted/20 flex items-center justify-end shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
